import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Button, Spin, Alert, Input } from "antd";
import { showSuccess, showError, showWarning, showInfo } from '../utils/notify';
import axios from "axios";
import welcomeImage from "./pictures/Freewifi.png";
import { useNavigate } from "react-router-dom";
import { ShoppingOutlined, CheckOutlined } from "@ant-design/icons";
import InstructionsModal from './admin/modals/InstructionsModal';

const PORTAL_FONT_FAMILY = '"Poppins", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif';

const safeJsonParse = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
};

const cleanVoucher = (value) =>
  String(value || '').replace(/[^A-Z0-9]/gi, '').toUpperCase();

const getCookieValue = (name) => {
  const nameEQ = name + '=';
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  return null;
};


// eslint-disable-next-line no-unused-vars -- retained for easy revert of the paid-UI hardcode
const computeIsPaidProject = (proj) => {
  if (!proj) return true;

  const toStartOfDay = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  };

  const isWithinDay = (from, to) => {
    const today = new Date();
    const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const fromDay = from ? toStartOfDay(from) : null;
    const toDay = to ? toStartOfDay(to) : null;
    const afterFrom = fromDay ? fromDay.getTime() <= todayDay.getTime() : true;
    const beforeTo = toDay ? todayDay.getTime() <= toDay.getTime() : true;
    return afterFrom && beforeTo;
  };

  const projStatePaid = String(proj?.proj_state || '').toLowerCase() === 'paid';
  if (!projStatePaid) return false;

  const projectWindowOk = isWithinDay(proj?.availability_from || proj?.availabilityFrom, proj?.availability_to || proj?.availabilityTo);
  if (!projectWindowOk) return true;

  const pairs = Array.isArray(proj?.price_duration_pairs) ? proj.price_duration_pairs : [];
  if (pairs.length === 0) return true;

  return pairs.some(p => isWithinDay(p.availability_from || p.availabilityFrom, p.availability_to || p.availabilityTo));
};

const detectIosCaptivePortalSafari = () => {
  const ua = (navigator.userAgent || "");
  const isAppleMobile = /iPad|iPhone|iPod/i.test(ua) || (/Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1);
  if (!isAppleMobile) return false;
  const hasCnaMarker = /CaptiveNetworkSupport/i.test(ua) || /wispr/i.test(ua);
  const hasWebKit = /AppleWebKit/i.test(ua);
  const hasKnownBrowserToken = /(Safari|CriOS|FxiOS|EdgiOS|OPiOS)/i.test(ua);
  const looksLikeCna = hasWebKit && !hasKnownBrowserToken;
  return hasCnaMarker || looksLikeCna;
};

const hasCompleteWiFiDogParams = (params) =>
  !!(params && params.gw_address && params.gw_port && params.gw_id && params.mac);

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) { /* fallback */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand && document.execCommand('copy');
    document.body.removeChild(ta);
    return !!ok;
  } catch (_) {
    return false;
  }
};

const WifiPortal = ({ API_BASE }) => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isWiFiDogContext, setIsWiFiDogContext] = useState(false);
  const [wifiDogParams, setWifiDogParams] = useState({});
  const [voucherExpiredMsg, setVoucherExpiredMsg] = useState(false);
  const [welcomeImageUrl, setWelcomeImageUrl] = useState(null);
  const [currentProjId, setCurrentProjId] = useState(null);
  const [projectBgUrl, setProjectBgUrl] = useState(null);
  const [hasConnected, setHasConnected] = useState(false);
  const [isPaidProject, setIsPaidProject] = useState(true);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [currentGwSn, setCurrentGwSn] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [connectDisabledSeconds, setConnectDisabledSeconds] = useState(30);
  const [loginLimitMessage, setLoginLimitMessage] = useState('');
  const [voucherErrorMessage, setVoucherErrorMessage] = useState('');
  const [noProjectForSsid, setNoProjectForSsid] = useState(false);
  const [isIosCaptivePortalSafari, setIsIosCaptivePortalSafari] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const navigate = useNavigate();
  const voucherInputRef = useRef(null);

  // Throttled resize: only re-render when crossing breakpoints used by styles (768, 1000)
  useEffect(() => {
    let rafId = null;
    const breakpointsCrossed = (a, b) => {
      const bands = (w) => `${w < 768 ? 'sm' : w < 1000 ? 'md' : 'lg'}`;
      return bands(a) !== bands(b);
    };
    const handleResize = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        const next = window.innerWidth;
        setWindowWidth((prev) => (breakpointsCrossed(prev, next) ? next : prev));
      });
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Defer third-party chat widget until after first paint
  useEffect(() => {
    let scriptEl = null;
    const inject = () => {
      let gwSnAttr = '';
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const fromUrl = urlParams.get('gw_sn') || urlParams.get('gwSn');
        const storedPars = safeJsonParse(localStorage.getItem('urlPars')) || {};
        gwSnAttr = fromUrl || storedPars.gw_sn || storedPars.gwSn || '';
        console.log('Injecting chat widget with gw_sn:', gwSnAttr);
      } catch (_) {
        gwSnAttr = '';
        console.warn('Failed to extract gw_sn for chat widget injection');
      }
      scriptEl = document.createElement('script');
      scriptEl.src = "https://yuask.me/widget-chat.js";
      scriptEl.async = true;
      scriptEl.setAttribute("data-identifier", "orange-wifi");
      scriptEl.setAttribute("data-name", "Orange Wifi");
      scriptEl.setAttribute(
        "data-image",
        "https://yuask.me/images/profiles/profile_20260406032139_a9d05a.webp"
      );
      scriptEl.setAttribute("data-color", "#e69718");
      scriptEl.setAttribute("data-show-button", "true");
      scriptEl.setAttribute("data-serial-number", gwSnAttr);
      document.body.appendChild(scriptEl);
    };
    const idleId = (typeof window.requestIdleCallback === 'function')
      ? window.requestIdleCallback(inject, { timeout: 2000 })
      : window.setTimeout(inject, 1500);
    return () => {
      if (typeof window.cancelIdleCallback === 'function' && typeof idleId === 'number') {
        window.cancelIdleCallback(idleId);
      } else {
        window.clearTimeout(idleId);
      }
      if (scriptEl && scriptEl.parentNode) scriptEl.parentNode.removeChild(scriptEl);
    };
  }, []);

  // Auto-focus voucher input when voucherCode is restored
  useEffect(() => {
    if (voucherCode && voucherInputRef?.current) {
      voucherInputRef.current.focus();
    }
  }, [voucherCode]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    let voucherFromUrl = null;
    try {
      voucherFromUrl =
        urlParams.get('voucher') ||
        urlParams.get('voucher_code') ||
        urlParams.get('code') ||
        urlParams.get('vc');
      if (voucherFromUrl && !voucherCode) {
        setVoucherCode(cleanVoucher(voucherFromUrl));
        setVoucherErrorMessage('');
        urlParams.delete('voucher');
        urlParams.delete('voucher_code');
        urlParams.delete('code');
        urlParams.delete('vc');
        const newQs = urlParams.toString();
        const newUrl = `${window.location.pathname}${newQs ? `?${newQs}` : ''}${window.location.hash || ''}`;
        window.history.replaceState({}, '', newUrl);
      }
    } catch (_) { /* ignore */ }

    if (!voucherFromUrl && !voucherCode) {
      const savedVoucher = sessionStorage.getItem('lastVoucherCode');
      if (savedVoucher && savedVoucher !== 'FREE_ACCESS') {
        setVoucherCode(cleanVoucher(savedVoucher));
      } else {
        const cookieVoucher = getCookieValue('voucherCode');
        if (cookieVoucher && cookieVoucher !== 'FREE_ACCESS') {
          setVoucherCode(cleanVoucher(cookieVoucher));
        }
      }
    }

    const gwAddress = urlParams.get('gw_address');
    const gwPort = urlParams.get('gw_port');
    const gwId = urlParams.get('gw_id');
    const gwSn = urlParams.get('gw_sn') || urlParams.get('gwSn');
    const mac = urlParams.get('mac');
    const expired = urlParams.get('expired');

    const storedPars = safeJsonParse(localStorage.getItem('urlPars')) || {};
    const effectiveGwAddress = gwAddress || storedPars.gw_address;
    const effectiveGwPort = gwPort || storedPars.gw_port;
    const effectiveGwId = gwId || storedPars.gw_id;
    const effectiveGwSn = gwSn || storedPars.gw_sn || storedPars.gwSn || '';
    const effectiveMac = mac || storedPars.mac;
    const effectiveUrl = urlParams.get('url') || storedPars.url || storedPars.original_url || '';

    try {
      setIsIosCaptivePortalSafari(detectIosCaptivePortalSafari());
    } catch (_) {
      setIsIosCaptivePortalSafari(false);
    }

    const urlParameters = {
      gw_address: effectiveGwAddress,
      gw_port: effectiveGwPort,
      gw_id: effectiveGwId,
      gw_sn: effectiveGwSn,
      mac: effectiveMac,
      url: effectiveUrl
    };

    localStorage.setItem('urlPars', JSON.stringify(urlParameters));
    setCurrentGwSn(effectiveGwSn || null);
    setNoProjectForSsid(false);

    if (expired === 'true') {
      setVoucherExpiredMsg(true);
      showWarning('Voucher expired', "⚠️ Your voucher has expired! Please enter a new voucher code to continue.");
      sessionStorage.removeItem('currentVoucher');
    }

    if (effectiveGwAddress && effectiveGwPort && effectiveGwId) {
      setIsWiFiDogContext(true);
      setWifiDogParams({
        gw_address: effectiveGwAddress,
        gw_port: effectiveGwPort,
        gw_id: effectiveGwId,
        gw_sn: effectiveGwSn,
        mac: effectiveMac,
        url: effectiveUrl
      });
    }
  }, []);

  // Connect-button cooldown
  useEffect(() => {
    if (connectDisabledSeconds <= 0) return;
    const timeoutId = setTimeout(() => {
      setConnectDisabledSeconds((s) => s - 1);
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [connectDisabledSeconds]);

  const getSponsors = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/sponsors/${currentProjId}`, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true
      });

      if (response.data) {
        const filteredSponsors = response.data.filter(s => s && (s.is_active === true || s.is_active === undefined));
        const sortedSponsors = [...filteredSponsors].sort((a, b) => {
          const at = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bt = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bt - at;
        });
        setSponsors(sortedSponsors);

        if (currentProjId) {
          showSuccess('Sponsors Retrieved', `(${filteredSponsors.length} for project ${currentProjId})`);
        } else {
          showSuccess('Sponsors Retrieved', `(${filteredSponsors.length} total)`);
        }
      }
    } catch (error) {
      console.error("Error fetching sponsors:", error);
      showError('Failed to fetch sponsors');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, currentProjId]);

  const getProjectBackground = useCallback(async () => {
    try {
      if (!currentGwSn) {
        setNoProjectForSsid(true);
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API_BASE}/api/projects/gw/${encodeURIComponent(currentGwSn)}`, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true
      });

      if (response.data?.success && response.data.data) {
        const proj = response.data.data;
        setCurrentProjId(proj.proj_id);
        // Hardcoded: always render the paid UI (voucher + buy) regardless of project config.
        setIsPaidProject(true);
        setProjectBgUrl(proj?.proj_bgpicture ? `${API_BASE}${proj.proj_bgpicture}` : null);
        setWelcomeImageUrl(proj?.proj_welcome_image ? `${API_BASE}${proj.proj_welcome_image}` : null);
        setNoProjectForSsid(false);
      } else {
        setNoProjectForSsid(true);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching project background:", error);
      setNoProjectForSsid(true);
      setLoading(false);
    }
  }, [API_BASE, currentGwSn]);

  const showLoginLimitReached = useCallback(() => {
    setLoginLimitMessage("You have already used your free WiFi trial today.To continue browsing, please buy a WiFi voucher.");
  }, []);

  const saveVoucherToSession = useCallback((code, expiryTime) => {
    if (code) sessionStorage.setItem('lastVoucherCode', code);
    // Persist session expiry so the static success.html countdown can read it (same origin).
    try {
      if (expiryTime) {
        localStorage.setItem('sessionExpiry', new Date(expiryTime).toISOString());
      } else {
        localStorage.removeItem('sessionExpiry');
      }
    } catch (_) {
      // localStorage may be unavailable (private mode); countdown just won't show.
    }
  }, []);

  const firstConnect = useCallback(async () => {
    setLoading(true);
    setLoginLimitMessage('');
    try {
      // Use the already-fetched isPaidProject state; getProjectBackground keeps it current.
      if (isPaidProject) {
        setLoading(false);
        showInfo('This is a paid WiFi project. Please buy or enter a voucher.');
        return;
      }
      if (!hasCompleteWiFiDogParams(wifiDogParams)) {
        showError('Missing captive portal context', 'Please connect through the WiFi login popup and try again.');
        return;
      }
      if (wifiDogParams?.mac) {
        try {
          await axios.post(`${API_BASE}/api/connections/record`, { mac: wifiDogParams.mac }, {
            headers: { "Content-Type": "application/json" },
            withCredentials: true
          });
        } catch (e) {
          console.warn('Failed to record connection', e?.message || e);
        }
      }

      const response = await axios.post(`${API_BASE}/api/free-access`, {
        gw_sn: wifiDogParams?.gw_sn,
        proj_id: currentProjId,
        gw_address: wifiDogParams?.gw_address,
        gw_port: wifiDogParams?.gw_port,
        gw_id: wifiDogParams?.gw_id,
        mac: wifiDogParams?.mac,
        url: wifiDogParams?.url
      }, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
        validateStatus: () => true
      });

      if (response.status === 429) {
        showLoginLimitReached(response?.data?.message);
      } else if (response.data?.success) {
        showSuccess('Internet access granted!');
        setLoginLimitMessage('');
        const voucherToSave = voucherCode || 'FREE_ACCESS';
        saveVoucherToSession(voucherToSave, response?.data?.expiryTime);
        const target = response.data.redirectUrl;
        if (target) {
          setTimeout(() => { window.location.href = target; }, 300);
        }
      } else {
        const msg = response.data?.message;
        if (typeof msg === 'string' && msg.toLowerCase().includes('login limit')) {
          showLoginLimitReached(msg);
        } else {
          showError('Unable to grant internet access', msg);
        }
      }
    } catch (error) {
      console.error("Error granting internet access:", error);
      if (error?.response?.status === 429) {
        showLoginLimitReached(error?.response?.data?.message);
      } else {
        showError('Failed to grant internet access');
      }
    } finally {
      setLoading(false);
      setHasConnected(false);
    }
  }, [API_BASE, isPaidProject, wifiDogParams, currentProjId, voucherCode, saveVoucherToSession, showLoginLimitReached]);

  const buildPaymentUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (wifiDogParams.gw_address) params.append('gw_address', wifiDogParams.gw_address);
    if (wifiDogParams.gw_port) params.append('gw_port', wifiDogParams.gw_port);
    if (wifiDogParams.gw_id) params.append('gw_id', wifiDogParams.gw_id);
    if (wifiDogParams.gw_sn) params.append('gw_sn', wifiDogParams.gw_sn);
    if (wifiDogParams.mac) params.append('mac', wifiDogParams.mac);
    if (wifiDogParams.url) {
      params.append('url', wifiDogParams.url);
      params.append('original_url', wifiDogParams.url);
    }
    const baseUrl = `${process.env.REACT_APP_BASE_URL}/page/payment-voucher`;
    const qs = params.toString();
    return qs ? `${baseUrl}?${qs}` : baseUrl;
  }, [wifiDogParams]);

  const handleBuyVoucher = useCallback(() => {
    try {
      const paymentUrl = buildPaymentUrl();
      const ua = navigator.userAgent || '';
      const isAndroid = /Android/i.test(ua);
      const isIOS = /iPhone|iPad|iPod/i.test(ua);
      const isMac = /Macintosh|Mac OS X/i.test(ua);
      const isSafari = /Safari/i.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
      const isWebView = /(wv|WebView)/i.test(ua) || (isAndroid && !/Chrome/i.test(ua));

      const openInNewTab = (url) => {
        const w = window.open(url, '_blank', 'noopener,noreferrer');
        if (!w) {
          const a = document.createElement('a');
          a.href = url;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      };

      if (isAndroid && isWebView) {
        const stripped = paymentUrl.replace(/^https?:\/\//, '');
        const intentUrl = `intent://${stripped}#Intent;scheme=https;action=android.intent.action.VIEW;end;`;
        window.location.href = intentUrl;
        setTimeout(() => openInNewTab(paymentUrl), 800);
        return;
      }

      if (isIOS || isIosCaptivePortalSafari) {
        window.location.href = paymentUrl;
        return;
      }
      if (isMac && isSafari) {
        window.location.href = paymentUrl;
        return;
      }

      openInNewTab(paymentUrl);
      showInfo('Opening payment page…');
    } catch (err) {
      console.error('Payment redirect failed:', err);
      window.location.href = buildPaymentUrl();
    }
  }, [buildPaymentUrl, isIosCaptivePortalSafari]);

  const handleCopyPaymentLink = useCallback(async () => {
    const url = buildPaymentUrl();
    const ok = await copyToClipboard(url);
    if (ok) {
      showSuccess('Link copied', 'Paste it in Safari to buy your voucher.');
    } else {
      showWarning('Copy failed', 'Please press and hold the link to copy it, then paste into Safari.');
    }
  }, [buildPaymentUrl]);

  const handleVoucherConfirm = useCallback(async () => {
    setVoucherLoading(true);
    const code = String(voucherCode || '').trim().toUpperCase();
    if (!code) {
      showWarning('Missing voucher', 'Please enter your voucher code.');
      setVoucherLoading(false);
      return;
    }
    setVoucherErrorMessage('');
    try {
      const storedPars = safeJsonParse(localStorage.getItem('urlPars')) || {};
      const effectiveParams = {
        gw_address: wifiDogParams?.gw_address || storedPars.gw_address,
        gw_port: wifiDogParams?.gw_port || storedPars.gw_port,
        gw_id: wifiDogParams?.gw_id || storedPars.gw_id,
        gw_sn: wifiDogParams?.gw_sn || storedPars.gw_sn || storedPars.gwSn,
        mac: wifiDogParams?.mac || storedPars.mac,
        url: wifiDogParams?.url || storedPars.url || storedPars.original_url
      };

      const hasFullWiFiDog = hasCompleteWiFiDogParams(effectiveParams);
      const payload = hasFullWiFiDog ? {
        voucher: code,
        gw_sn: effectiveParams.gw_sn,
        gw_address: effectiveParams.gw_address,
        gw_port: effectiveParams.gw_port,
        gw_id: effectiveParams.gw_id,
        mac: effectiveParams.mac,
        url: effectiveParams.url
      } : { voucher: code };

      const response = await axios.post(`${API_BASE}/api/auth`, payload, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
        validateStatus: () => true
      });

      if (response?.data?.success) {
        showSuccess('Voucher verified successfully!');
        document.cookie = 'voucherCode=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT';
        saveVoucherToSession(code, response?.data?.expiryTime);
        const target = response?.data?.redirectUrl || response?.data?.finalRedirect;
        if (target) {
          setTimeout(() => { window.location.href = target; }, 300);
        } else if (hasFullWiFiDog) {
          const token = response?.data?.token || 'authenticated';
          const wifidogUrl = `http://${effectiveParams.gw_address}:${effectiveParams.gw_port}/wifidog/auth?token=${encodeURIComponent(token)}`;
          setTimeout(() => { window.location.href = wifidogUrl; }, 300);
        } else {
          showInfo('Voucher verified. You now have internet access.');
        }
      } else {
        const status = response?.status;
        const msg = response?.data?.message || response?.data?.error || 'Invalid voucher code';
        const errorCode = response?.data?.code;
        setVoucherLoading(false);
        if (status === 409) {
          const errorMsg = msg || 'This voucher is already being used on another device. Please use a different voucher or wait for the current session to end.';
          setVoucherErrorMessage(errorMsg);
          showError('Voucher Already in Use', errorMsg);
        } else if (status === 401 || errorCode === 'VOUCHER_NOT_VALID_FOR_SITE') {
          if (msg.toLowerCase().includes('expired')) {
            const errorMsg = 'This voucher has expired and can no longer be used. Please purchase a new voucher.';
            setVoucherErrorMessage(errorMsg);
            showError('Voucher Expired', errorMsg);
          } else if (errorCode === 'VOUCHER_NOT_VALID_FOR_SITE') {
            setVoucherErrorMessage(msg);
            showError('Voucher Not Valid for This Site', msg);
          } else {
            setVoucherErrorMessage(msg);
            showError('Invalid Voucher', msg);
          }
        } else if (status === 400) {
          if (msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('inactive')) {
            const errorMsg = 'This voucher is expired or inactive and cannot be used. Please purchase a new voucher.';
            setVoucherErrorMessage(errorMsg);
            showError('Voucher Not Available', errorMsg);
          } else {
            setVoucherErrorMessage(msg);
            showError('Voucher Error', msg);
          }
        } else if (status === 404) {
          const errorMsg = 'Voucher not found. Please check your voucher code and try again.';
          setVoucherErrorMessage(errorMsg);
          showError('Voucher Not Found', errorMsg);
        } else if (status === 429) {
          showLoginLimitReached(msg);
        } else if (status === 500) {
          const errorMsg = 'There was an error processing your voucher. Please try again later.';
          setVoucherErrorMessage(errorMsg);
          showError('Server Error', errorMsg);
        } else {
          setVoucherErrorMessage(msg);
          showError('Voucher Verification Failed', msg);
        }
      }
    } catch (e) {
      console.error('Voucher confirm failed', e);
      const serverMsg = e?.response?.data?.message || e?.response?.data?.error || e?.message;
      if (e?.code === 'NETWORK_ERROR' || e?.message?.includes('Network Error')) {
        const errorMsg = 'Unable to connect to the server. Please check your internet connection and try again.';
        setVoucherErrorMessage(errorMsg);
        showError('Connection Error', errorMsg);
      } else if (e?.response?.status === 429) {
        showLoginLimitReached(serverMsg);
      } else {
        const errorMsg = serverMsg || 'An unexpected error occurred. Please try again.';
        setVoucherErrorMessage(errorMsg);
        showError('Failed to Verify Voucher', errorMsg);
      }
      setVoucherLoading(false);
    }
  }, [API_BASE, voucherCode, wifiDogParams, saveVoucherToSession, showLoginLimitReached]);

  useEffect(() => {
    if (currentProjId) getSponsors();
  }, [currentProjId, getSponsors]);

  useEffect(() => {
    if (currentGwSn) getProjectBackground();
  }, [currentGwSn, getProjectBackground]);

  // Safety net: avoid indefinite spinner if no project resolved
  useEffect(() => {
    const timerId = setTimeout(() => {
      if (loading && !currentProjId) {
        setNoProjectForSsid(true);
        setLoading(false);
      }
    }, 6000);
    return () => clearTimeout(timerId);
  }, [loading, currentProjId, currentGwSn]);

  // Connectivity check: poll every 5s for 204, only when tab is visible
  useEffect(() => {
    const connectivityUrl = 'http://connectivitycheck.gstatic.com/generate_204';
    let isCancelled = false;

    const checkConnectivity = async () => {
      if (isCancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      try {
        const response = await axios.get(connectivityUrl, {
          validateStatus: (status) => status === 204,
          timeout: 4000
        });
        if (response.status === 204 && !isCancelled) {
          // Carry the session expiry in the URL so the countdown survives even if
          // success.html is reached on a different origin (where localStorage is invisible).
          let successUrl = '/success.html';
          try {
            const expiry = localStorage.getItem('sessionExpiry');
            if (expiry) successUrl += `?expiry=${encodeURIComponent(expiry)}`;
          } catch (_) {}
          window.location.href = successUrl;
        }
      } catch (_) {
        // expected while behind captive portal
      }
    };

    const intervalId = setInterval(checkConnectivity, 5000);
    checkConnectivity();

    return () => {
      isCancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  // Memoized style objects keyed on windowWidth
  const containerStyle = useMemo(() => ({
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    textAlign: "center",
    padding: "5px",
    paddingBottom: "32px",
    background: '#faf8ee',
    position: "relative",
    marginLeft: windowWidth < 768 ? 0 : undefined,
    color: "#000000",
    fontFamily: PORTAL_FONT_FAMILY,
  }), [windowWidth]);

  const contentStyle = useMemo(() => ({
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: "1200px",
  }), []);

  const ctaContainerStyle = useMemo(() => ({
    position: "relative",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    width: "100%",
    padding: windowWidth < 768 ? "40px 10px" : "40px 20px",
    margin: windowWidth < 768 ? "30px auto 20px" : "30px auto",
  }), [windowWidth]);

  // Platinum sponsors — seamless infinite ticker, works on all screen sizes
  // When a logo is half-cut on the left, its clone is half-visible on the right
  const platinumCarousel = useMemo(() => {
    const dataSponsors = sponsors.filter(
      s => (s.type || '').toLowerCase() === 'platinum'
    );

    const cardH = windowWidth < 768 ? 120 : 160;
    const gap   = windowWidth < 768 ? 18  : 30;

    const platinumSponsors = dataSponsors;

    // Nothing to show when there are no platinum sponsors — avoids a divide-by-zero
    // in copiesNeeded below (setWidth would be 0).
    if (platinumSponsors.length === 0) return null;

    const cardW = windowWidth < 768 ? 190 : 260;
    const itemW = () => cardW;

    // Width of one natural set of logos.
    const setWidth = platinumSponsors.reduce((sum, s) => sum + itemW(s) + gap, 0);

    // Repeat logos enough times so one "row" is always wider than the viewport.
    // This prevents gaps on desktop/tablet where setWidth < windowWidth.
    const copiesNeeded = Math.max(2, Math.ceil(windowWidth / setWidth) + 1);
    const fullRow = Array.from({ length: copiesNeeded }, () => platinumSponsors).flat();
    const fullRowWidth = fullRow.reduce((sum, s) => sum + itemW(s) + gap, 0);

    // Speed ~70px/s
    const duration = Math.max(6, Math.round(fullRowWidth / 70));

    const logoCard = (sponsor, key) => {
      const imgSrc = sponsor.image
        ? (sponsor.image.startsWith('http') ? sponsor.image : `${API_BASE}${sponsor.image}`)
        : null;
      return (
        <div
          key={key}
          style={{
            flexShrink: 0,
            width: itemW(sponsor),
            height: cardH,
            marginRight: gap,
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          {imgSrc
            ? <img
                src={imgSrc}
                alt={sponsor.name}
                loading="lazy"
                decoding="async"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  padding: windowWidth < 768 ? '4px 6px' : '6px 8px',
                  boxSizing: 'border-box',
                }}
                onError={e => { e.target.style.display = 'none'; }}
              />
            : <span style={{ fontSize: 11, fontWeight: 700, color: '#555', textAlign: 'center', padding: 6 }}>
                {sponsor.name}
              </span>
          }
        </div>
      );
    };

    return (
      <div style={{ width: '100%', margin: '32px auto 8px', overflow: 'hidden' }}>
        <style>{`
          /*
           * Row A + Row B, each = fullRowWidth (>= viewport width on any screen).
           * Keyframe shifts by fullRowWidth — when A exits, B is exactly where A was.
           * Mirror: logo half-cut on left = same logo half-visible on right.
           * No gap on mobile, tablet, or desktop.
           */
          @keyframes platTicker {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-${fullRowWidth}px); }
          }
          .plat-ticker {
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
            width: max-content;
            animation: platTicker ${duration}s linear infinite;
            will-change: transform;
          }
          .plat-ticker:hover { animation-play-state: paused; }
        `}</style>

        <div className="plat-ticker">
          {fullRow.map((s, i) => logoCard(s, `a-${i}-${s._id || s.name}`))}
          {fullRow.map((s, i) => logoCard(s, `b-${i}-${s._id || s.name}`))}
        </div>
      </div>
    );
  }, [sponsors, windowWidth, API_BASE]);

  return (
    <>
      {loading && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "rgba(0,0,0,0.6)",
          zIndex: 9999
        }}>
          <Spin size="large" tip="Loading..." />
        </div>
      )}
      <div className="wifi-portal-root" style={containerStyle}>
        <style>{`
          .wifi-portal-root,
          .wifi-portal-root * {
            font-family: ${PORTAL_FONT_FAMILY} !important;
          }
        `}</style>
        <div style={{ ...contentStyle, flex: 1 }}>
          {noProjectForSsid && (
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "60vh",
              gap: "16px"
            }}>
              <img
                alt="No project available for this WiFi"
                style={{
                  maxWidth: "90%",
                  width: "560px",
                  height: "auto",
                  borderRadius: "12px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
                  background: "rgba(255,255,255,0.85)"
                }}
                src={`data:image/svg+xml;utf8,${encodeURIComponent(
                  `<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='400'>
                    <defs>
                      <linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
                        <stop offset='0%' stop-color='#1f2937'/>
                        <stop offset='100%' stop-color='#111827'/>
                      </linearGradient>
                    </defs>
                    <rect width='100%' height='100%' fill='url(#g)' rx='24' ry='24'/>
                    <g>
                      <text x='50%' y='48%' dominant-baseline='middle' text-anchor='middle' fill='#FDE68A' font-family='Poppins, Segoe UI, Roboto, Arial' font-size='44' font-weight='700'>No project available for this WiFi</text>
                      <text x='50%' y='65%' dominant-baseline='middle' text-anchor='middle' fill='#E5E7EB' font-family='Poppins, Segoe UI, Roboto, Arial' font-size='20'>Please contact the administrator to assign a project to this SSID.</text>
                    </g>
                  </svg>`
                )}`}
              />
            </div>
          )}

          {!noProjectForSsid && (
            <div style={ctaContainerStyle}>
              <img
                src={welcomeImageUrl || welcomeImage}
                alt="Welcome to Free WiFi"
                decoding="async"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  maxHeight: "250px",
                  objectFit: "contain"
                }}
              />

              <InstructionsModal
                visible={showInstructions}
                onCancel={() => setShowInstructions(false)}
              />

              {loginLimitMessage && (
                <div style={{ width: "100%", maxWidth: "640px", margin: "0 auto 12px", padding: "0 12px" }}>
                  <Alert
                    type="warning"
                    message={loginLimitMessage}
                    showIcon
                    style={{
                      background: "rgba(255, 193, 7, 0.15)",
                      borderColor: "rgba(255, 193, 7, 0.35)",
                      color: "#000000",
                    }}
                  />
                </div>
              )}
              {voucherErrorMessage && (
                <div style={{ width: "100%", maxWidth: "640px", margin: "0 auto 12px", padding: "0 12px" }}>
                  <Alert
                    type="error"
                    message={voucherErrorMessage}
                    showIcon
                    closable
                    onClose={() => setVoucherErrorMessage('')}
                    style={{
                      background: "rgba(255, 77, 79, 0.15)",
                      borderColor: "rgba(255, 77, 79, 0.35)",
                      color: "#000000",
                    }}
                  />
                </div>
              )}
              {!isPaidProject ? (
                <Button
                  type="primary"
                  size="large"
                  onClick={() => { if (!hasConnected) { setHasConnected(true); firstConnect(); } }}
                  disabled={connectDisabledSeconds > 0}
                  style={{
                    background: "linear-gradient(45deg, #FFD700, #FFA500)",
                    color: "#000",
                    border: "none",
                    fontWeight: "bold",
                    fontSize: "clamp(16px, 3vw, 18px)",
                    borderRadius: "30px",
                    padding: "16px 45px",
                    minHeight: "55px",
                    boxShadow: "0 6px 20px rgba(255, 215, 0, 0.5)",
                    transition: "all 0.3s ease",
                    textShadow: "0 1px 2px rgba(255, 255, 255, 0.5)",
                  }}
                >
                  {connectDisabledSeconds > 0
                    ? `PLEASE WAIT ${connectDisabledSeconds}s`
                    : 'CLICK TO CONNECT'}
                </Button>
              ) : (
                <div style={{ width: '100%', maxWidth: '520px', padding: '0 10px', margin: '0 auto' }}>
                  <div style={{
                    width: '100%',
                    background: 'rgba(255, 255, 255, 0.38)',
                    borderRadius: '16px',
                    padding: windowWidth < 768 ? '18px 14px' : '24px 20px',
                    border: '1px solid rgba(255, 255, 255, 0.55)',
                    backdropFilter: 'blur(14px) saturate(140%)',
                    WebkitBackdropFilter: 'blur(14px) saturate(140%)',
                    boxShadow: '0 10px 28px rgba(31, 41, 55, 0.16)',
                    fontFamily: PORTAL_FONT_FAMILY,
                  }}>
                    <div style={{
                      textAlign: 'left',
                      color: '#1f2937',
                      fontWeight: 500,
                      fontSize: '20px',
                      lineHeight: 1,
                      marginBottom: '10px',
                    }}>
                      Already have a voucher?
                    </div>

                    <Input
                      ref={voucherInputRef}
                      value={voucherCode}
                      onChange={(e) => {
                        setVoucherCode(cleanVoucher(e.target.value));
                        if (voucherErrorMessage) setVoucherErrorMessage('');
                      }}
                      placeholder="Enter Voucher Code"
                      size="large"
                      maxLength={32}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="characters"
                      inputMode="text"
                      autoFocus
                      style={{
                        borderRadius: '10px',
                        width: '100%',
                        height: '48px',
                        fontWeight: 600,
                        fontSize: '18px',
                        textAlign: 'Center',
                        textTransform: 'uppercase',
                        color: '#111827',
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                        marginBottom: isIosCaptivePortalSafari ? '8px' : '12px',
                      }}
                    />

                    <Button
                      type="default"
                      size="large"
                      loading={voucherLoading}
                      disabled={voucherLoading || !String(voucherCode || '').trim()}
                      onClick={handleVoucherConfirm}
                      icon={<CheckOutlined />}
                      style={{
                        width: '100%',
                        height: '50px',
                        borderRadius: '30px',
                        border: 'none',
                        background: '#ff9f01',
                        color: '#fff',
                        fontWeight: 800,
                        fontSize: '24px',
                        letterSpacing: '0.5px',
                        boxShadow: '0 6px 14px rgba(255, 159, 1, 0.35)',
                      }}
                    >
                      CONNECT NOW
                    </Button>

                    <div style={{
                      marginTop: '8px',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontSize: '13px',
                      fontWeight: 600,
                    }}>
                      Enter the voucher code sent to your email.
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      margin: '14px 0',
                    }}>
                      <div style={{ flex: 1, height: '1px', background: '#d1d5db' }} />
                      <div style={{ color: '#374151', fontWeight: 700, fontSize: '16px' }}>OR</div>
                      <div style={{ flex: 1, height: '1px', background: '#d1d5db' }} />
                    </div>

                    <div style={{ textAlign: 'left', marginBottom: '10px' }}>
                      <div style={{ color: '#1f2937', fontWeight: 500, fontSize: '20px', lineHeight: 1 }}>
                        Need internet access?
                      </div>
                    </div>

                    <Button
                      type="primary"
                      size="large"
                      onClick={handleBuyVoucher}
                      icon={<ShoppingOutlined />}
                      style={{
                        width: '100%',
                        height: '52px',
                        borderRadius: '30px',
                        border: 'none',
                        background: '#c2510a',
                        color: '#fff',
                        fontWeight: 800,
                        fontSize: '24px',
                        letterSpacing: '0.5px',
                        boxShadow: '0 6px 14px rgba(194, 81, 10, 0.35)',
                      }}
                    >
                      GET WIFI VOUCHER
                    </Button>
                  </div>
                </div>
              )}

              {/* Platinum sponsors carousel — shown below CTA for all project types */}
              {platinumCarousel}
            </div>
          )}
        </div>

        {/* National Privacy Commission DPO/DPS Registered badge — pinned in the bottom-right corner, under the yuask.me chat widget */}
        <img
          src={`${process.env.PUBLIC_URL}/Picsart_26-07-06_16-57-57-001.png`}
          alt="National Privacy Commission DPO/DPS Registered"
          loading="lazy"
          decoding="async"
          style={{
            position: "fixed",
            left: windowWidth < 768 ? "-4px" : "8px",
            bottom: windowWidth < 768 ? "-25px" : "-25px",
            width: windowWidth < 768 ? "120px" : "150px",
            height: "auto",
            zIndex: 2147483647,
            pointerEvents: "none",
            //filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.25))",
          }}
        />
      </div>
    </>
  );
};

export default WifiPortal;
